<script lang="ts">
    // "Sign In" form.
    import { onDestroy, onMount } from 'svelte'
    import { writable, type Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import { lg } from 'hydria/services/language'

    import { signinBlockPage } from './globalContext'

    let { goto } = $props()

    // Clear the error after some time
    const CLEAR_ERROR_AFTER_MS = 7500

    // Bunch of functions to call on destroy, like store subscriptions.
    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const schema = yup.object().shape({
        email: yup.string().email().required().label('Email'),
    })

    interface IFields {
        email: string
    }
    const initialFields: IFields = { email: '' }
    const fields: IFields = $state({ ...initialFields })

    // When true we are signing in, (e.g. show a spinner)
    let communicating = writable(false)
    let emailSent = writable(false)

    function lgoto(where: 'signin-in' | 'welcome' | 'signin' | 'lost-password' | 'register') {
        $emailSent = false
        fields.email != ''
        return goto(where)
    }

    // When not null, there was an error to show.
    // That error is shown for a few seconds or until a value changes.
    const error: Writable<undefined | string> = writable(undefined)
    let errorTimer: ReturnType<typeof setTimeout> | null

    function clearErrorTimer() {
        if (errorTimer) {
            clearTimeout(errorTimer)
            errorTimer = null
        }
    }
    function clearError() {
        if ($error != null) {
            clearErrorTimer()
            error.set(undefined)
        }
    }

    unsubs.push(
        error.subscribe((newError) => {
            // This is called when the `error` store changes
            clearErrorTimer()
            if (newError != null) {
                errorTimer = setTimeout(() => {
                    errorTimer = null
                    clearError()
                }, CLEAR_ERROR_AFTER_MS)
            }
        }),
    )

    // Set 'isvalid' variables based on their validity when a value changes
    let emailIsValid = $derived((schema.fields.email as yup.StringSchema).isValidSync(fields.email))
    let formIsValid = $derived(schema.isValidSync(fields))

    let emailIsDirty = $state(false)

    $effect(() => {
        if (fields.email != '' && !emailIsDirty) emailIsDirty = true
    })

    // We setup a store so we can easily do stuff when anything changes in the fields.
    // The purpose being to clear the error if there’s one.
    let formData = writable(fields)
    $effect(() => {
        $formData = fields
    })
    unsubs.push(
        formData.subscribe((nv) => {
            if (errorTimer != null) {
                clearError()
            }
        }),
    )

    async function reqPassword(email: string) {
        let fdata = new Blob([JSON.stringify({ email: email }, null, 0)], { type: 'application/json' })
        const headers = new Headers()
        headers.append('Accept', 'application/json')
        headers.append('X-Lg', $lg)

        const r = await fetch(new Request($signinBlockPage == 'register' ? '/json/session/1/user' : '/json/session/1/password/reset'), {
            method: 'POST',
            headers: headers,
            body: fdata,
        })

        let data: any
        try {
            data = await r.json()
        } catch {}

        if (r.status != 200) {
            throw { success: false, error: data && data.error ? data.error : null, ecode: data && data.ecode ? data.ecode : null }
        }

        if (data.success && (data.ecode == 'EMAIL_SENT' || $signinBlockPage == 'register')) {
            return { success: true }
        }
        throw { success: false, error: data && data.error ? data.error : null, ecode: data && data.ecode ? data.ecode : null }
    }

    // Send an email
    async function formSubmit(event: Event) {
        event.preventDefault()
        if (!formIsValid) {
            return
        }
        error.set(undefined)
        communicating.set(true)
        let res
        try {
            res = await reqPassword(fields.email)
        } catch (e) {
            res = e
        }
        communicating.set(false)
        if (!res.success) {
            if (res.ecode === 'EMAIL_IN_USE') error.set($lg == 'fr' ? 'Cette adresse courriel est déjà en fonction' : 'This email address is already in use')
            else if (res.error) error.set(res.error)
            else error.set($lg == 'fr' ? 'L’opération n’a pas pu être effectuée' : 'The operation could not be completed')
        } else {
            emailSent.set(true)
        }
    }

    let mainInput: HTMLElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })
</script>

<form novalidate onsubmit={formSubmit}>
    <div class="logo-text">
        {#if $signinBlockPage != 'register'}
            {$lg == 'fr' ? 'Mot de passe oublié ?' : 'Lost password?'}
        {:else}
            {$lg == 'fr' ? 'Créer un compte' : 'Create an account'}
        {/if}
    </div>

    {#if $emailSent}
        {#if $signinBlockPage == 'register'}
            <p>
                {#if $lg == 'fr'}
                    Un courriel a été envoyé à {fields.email}, contenant un lien permettant poursuivre la création du compte.
                {:else}
                    An email has been sent to {fields.email}, containing a link allowing to continue the registration process.
                {/if}
            </p>
        {:else}
            <p>
                {#if $lg == 'fr'}
                    Si un compte existe avec l’adresse {fields.email}, vous recevrez un courriel contenant un lien permettant d’appliquer un nouveau mot de passe à votre compte.
                {:else}
                    If an account exists with the email address {fields.email}, you will receive an email containing a link to a page allowing you to set a new password.
                {/if}
            </p>
        {/if}
        {#if $lg == 'fr'}
            <p>Le courriel devrait arriver sous peu. Assurez-vous aussi de vérifier dans votre boîte de messages indésirables.</p>
        {:else}
            <p>The email should arrive shortly. Also be sure to check your spam box.</p>
        {/if}
    {:else}
        <div class="inp-email">
            <label for="email" class="sr-only">{$lg == 'fr' ? 'Adresse électronique' : 'Email address'}</label>
            <div class="input-group">
                <span class="input-group-text"><Icon inline icon="fa:user" /></span>
                <input class="form-control" id="email" type="email" bind:value={fields.email} disabled={$communicating} bind:this={mainInput} oninput={() => clearError()} class:is-valid={emailIsValid} class:is-invalid={!emailIsValid && emailIsDirty} />
            </div>
        </div>

        {#if $error != null}
            <div class="error">
                <div class="alert alert-danger">{$error}</div>
            </div>
        {:else}
            <div class="inp-action d-grid">
                <button class="btn btn-primary" type="submit" disabled={!formIsValid || $communicating || $error != null}>
                    {#if $communicating}{$lg == 'fr' ? 'Veuillez patienter…' : 'Validating…'}
                    {:else if $signinBlockPage == 'register'}{$lg == 'fr' ? 'Créer le compte' : 'Create account'}
                    {:else}{$lg == 'fr' ? 'Changer le mot de passe' : 'Reset password'}
                    {/if}
                </button>
            </div>
        {/if}
    {/if}

    {#if !$communicating}
        <div class="inp-action d-grid">
            <button class="btn btn-link" type="button" onclick={() => lgoto('signin')}>{$lg == 'fr' ? 'Se connecter' : 'Sign in'}</button>
        </div>
    {/if}
    {#if !$communicating && $signinBlockPage != 'register'}
        <div class="inp-action d-grid">
            <button class="btn btn-link" type="button" onclick={() => lgoto('register')}>{$lg == 'fr' ? 'Créer un nouveau compte' : 'Create a new account'}</button>
        </div>
    {:else if !$communicating && $signinBlockPage == 'register'}
        <div class="inp-action d-grid">
            <button class="btn btn-link" type="button" onclick={() => lgoto('lost-password')}>{$lg == 'fr' ? 'Mot de passe oublié ?' : 'Lost password?'}</button>
        </div>
    {/if}
</form>
